import type { ThreeSidePathsType } from "@tosize/shapes/src/types/three";
import { EllipseCurve, Path, Vector2 } from "three";
import { RENDER_QUALITY } from "../generic-shape/Shape";

const DISTANCE_FROM_SHAPE = 0.05 * 2;
export const ENDLINE_LENGTH = DISTANCE_FROM_SHAPE / 2 / 2;
export const LINE_WIDTH = 1;
export const LINE_COLOR = "#cccccc";
export const ACTIVE_LINE_COLOR = "#EFBA1A";
export const TEXT_COLOR = "#555555";
export const FONT_SIZE = (38 * 1.75) / 1000;
export const OFFSET = DISTANCE_FROM_SHAPE + ENDLINE_LENGTH;

export type LineType = "closed" | "arc" | "horizontal" | "vertical" | "angled";

export function getOuterDimensions(sides: ThreeSidePathsType[]) {
	const biggestX = Math.max(
		...sides.flatMap((line) => line.path.getPoints(RENDER_QUALITY).map((point) => point.x)),
	);
	const smallestX = Math.min(
		...sides.flatMap((line) => line.path.getPoints(RENDER_QUALITY).map((point) => point.x)),
	);
	const biggestY = Math.max(
		...sides.flatMap((line) => line.path.getPoints(RENDER_QUALITY).map((point) => point.y)),
	);
	const smallestY = Math.min(
		...sides.flatMap((line) => line.path.getPoints(RENDER_QUALITY).map((point) => point.y)),
	);

	return { biggestX, smallestX, biggestY, smallestY };
}

export function getPoints(path: Path) {
	const points = path.getPoints(RENDER_QUALITY);
	const firstPoint = points[0]!;
	const lastPoint = points[points.length - 1]!;

	const middlePoint =
		points.length === 2
			? new Vector2((firstPoint.x + lastPoint.x) / 2, (firstPoint.y + lastPoint.y) / 2)
			: points[Math.ceil(points.length / 2) - 1]!;

	// Map and convert each point to fixed(2)
	const firstPointFixed = new Vector2(+firstPoint.x.toFixed(10), +firstPoint.y.toFixed(10));
	const middlePointFixed = new Vector2(+middlePoint.x.toFixed(10), +middlePoint.y.toFixed(10));
	const lastPointFixed = new Vector2(+lastPoint.x.toFixed(10), +lastPoint.y.toFixed(10));

	return {
		firstPoint: firstPointFixed,
		lastPoint: lastPointFixed,
		middlePoint: middlePointFixed,
	};
}

interface LinePositionType {
	firstPoint: Vector2;
	lastPoint: Vector2;
	middlePoint?: Vector2;
}

export function getLinePosition(points: LinePositionType) {
	if (points.firstPoint.x > points.lastPoint.x && points.firstPoint.y > points.lastPoint.y) {
		return "leftTop";
	}
	if (points.firstPoint.x < points.lastPoint.x && points.firstPoint.y > points.lastPoint.y) {
		return "leftBottom";
	}
	if (points.firstPoint.x > points.lastPoint.x && points.firstPoint.y < points.lastPoint.y) {
		return "rightTop";
	}
	if (points.firstPoint.x < points.lastPoint.x && points.firstPoint.y < points.lastPoint.y) {
		return "rightBottom";
	}
	return "other";
}

export function addPathEndlines(path: Path, endLineLength: number): Path {
	const { firstPoint, middlePoint, lastPoint } = getPoints(path);
	const newPath = new Path();

	// Start of line
	// Right bottom
	if (firstPoint.x < lastPoint.x && firstPoint.y < lastPoint.y) {
		newPath
			.moveTo(firstPoint.x, firstPoint.y + endLineLength)
			.lineTo(firstPoint.x, firstPoint.y - endLineLength);
	}
	// Left bottom
	if (firstPoint.x < lastPoint.x && firstPoint.y > lastPoint.y) {
		newPath
			.moveTo(firstPoint.x + endLineLength, firstPoint.y)
			.lineTo(firstPoint.x - endLineLength, firstPoint.y);
	}
	// Left top
	if (firstPoint.x > lastPoint.x && firstPoint.y > lastPoint.y) {
		newPath
			.moveTo(firstPoint.x, firstPoint.y - endLineLength)
			.lineTo(firstPoint.x, firstPoint.y + endLineLength);
	}
	// Right top
	if (
		firstPoint.x > lastPoint.x &&
		(firstPoint.y < lastPoint.y ||
			// This is a half circle
			(path.curves[0] instanceof EllipseCurve &&
				Math.abs(+(path.curves[0].aStartAngle - path.curves[0].aEndAngle).toFixed(5)) ===
					+Math.PI.toFixed(5)))
	) {
		newPath
			.moveTo(firstPoint.x - endLineLength, firstPoint.y)
			.lineTo(firstPoint.x + endLineLength, firstPoint.y);
	}

	// Horizontal first
	if (
		firstPoint.x !== lastPoint.x &&
		firstPoint.y === lastPoint.y &&
		lastPoint.y === middlePoint.y
	) {
		newPath
			.moveTo(firstPoint.x, firstPoint.y - endLineLength)
			.lineTo(firstPoint.x, firstPoint.y + endLineLength);
	}
	// Vertical first
	if (firstPoint.x === lastPoint.x && firstPoint.y !== lastPoint.y) {
		newPath
			.moveTo(firstPoint.x - endLineLength, firstPoint.y)
			.lineTo(firstPoint.x + endLineLength, firstPoint.y);
	}

	// Original lines
	path.curves.map((curve) => {
		newPath.curves.push(curve);
	});

	// End of line
	// Right bottom
	if (firstPoint.x < lastPoint.x && firstPoint.y < lastPoint.y) {
		newPath
			.moveTo(lastPoint.x - endLineLength, lastPoint.y)
			.lineTo(lastPoint.x + endLineLength, lastPoint.y);
	}
	// Left bottom
	if (firstPoint.x < lastPoint.x && firstPoint.y > lastPoint.y) {
		newPath
			.moveTo(lastPoint.x, lastPoint.y + endLineLength)
			.lineTo(lastPoint.x, lastPoint.y - endLineLength)
			.moveTo(lastPoint.x, lastPoint.y);
	}
	// Left top
	if (
		firstPoint.x > lastPoint.x &&
		(firstPoint.y > lastPoint.y ||
			// This is a half circle
			(path.curves[0] instanceof EllipseCurve &&
				Math.abs(+(path.curves[0].aStartAngle - path.curves[0].aEndAngle).toFixed(5)) ===
					+Math.PI.toFixed(5)))
	) {
		newPath
			.moveTo(lastPoint.x + endLineLength, lastPoint.y)
			.lineTo(lastPoint.x - endLineLength, lastPoint.y);
	}
	// Right top
	if (firstPoint.x > lastPoint.x && firstPoint.y < lastPoint.y) {
		newPath
			.moveTo(lastPoint.x, lastPoint.y - endLineLength)
			.lineTo(lastPoint.x, lastPoint.y + endLineLength);
	}
	// Horizontal last
	if (
		firstPoint.x !== lastPoint.x &&
		firstPoint.y === lastPoint.y &&
		lastPoint.y === middlePoint.y
	) {
		newPath
			.moveTo(lastPoint.x, lastPoint.y - endLineLength)
			.lineTo(lastPoint.x, lastPoint.y + endLineLength);
	}
	// Vertical last
	if (firstPoint.x === lastPoint.x && firstPoint.y !== lastPoint.y) {
		newPath
			.moveTo(lastPoint.x - endLineLength, lastPoint.y)
			.lineTo(lastPoint.x + endLineLength, lastPoint.y);
	}

	return newPath;
}

export function isActiveLine(code: string, current?: string) {
	if (current?.includes("combined")) {
		const id = current.split("_").at(0) as string;

		return code.includes(id) || current === "combined";
	}

	return code === current;
}
